/**
 * Plotea legal documents (`/privacy`, `/terms`). DRAFTS: the owner and partner
 * approve them, ideally with a lawyer, before `PLOTEA_LEGAL_DRAFT` turns off.
 * Bracketed placeholders are company details still to be supplied.
 * Format: the block markdown subset rendered by `TermsDocument`.
 */

/** Shows the draft banner on both documents until the texts are approved. */
export const PLOTEA_LEGAL_DRAFT = true

export const PLOTEA_LEGAL_UPDATED = '2 listopada 2026'

const COMPANY =
  'Digital Wanderer Sp. z o.o. z siedzibą w [ADRES SIEDZIBY], wpisana do rejestru przedsiębiorców KRS pod numerem [KRS], NIP [NIP]'
const CONTACT = 'kontakt@plotea.com'

export const ploteaPrivacyPolicy = `
# Polityka prywatności Plotea

## 1. Administrator danych

Administratorem Twoich danych osobowych jest ${COMPANY} („Administrator”, „my”). W sprawach dotyczących danych osobowych napisz na ${CONTACT} lub na adres siedziby.

## 2. Jakie dane przetwarzamy

- Dane konta: imię i nazwisko, adres e-mail, hasło (przechowywane wyłącznie w postaci skrótu kryptograficznego), identyfikator konta u dostawcy logowania (Google lub Microsoft), jeśli z niego korzystasz.
- Dane organizacji: nazwa organizacji, role członków, zaproszenia oraz informacje o planie i dostępie do usługi.
- Dane korzystania z usługi: analizowane działki, zapisane analizy, dokumenty, ustawienia i notatki, które wprowadzasz w Plotea.
- Dane techniczne: adres IP, informacje o przeglądarce, identyfikatory sesji i dzienniki zdarzeń serwera, potrzebne do działania i zabezpieczenia usługi.
- Korespondencja: treść wiadomości, które do nas wysyłasz.

## 3. Cele i podstawy prawne

- Założenie i prowadzenie konta oraz świadczenie usługi Plotea na podstawie umowy (art. 6 ust. 1 lit. b RODO), a gdy działasz w imieniu klienta będącego przedsiębiorcą, na podstawie naszego prawnie uzasadnionego interesu polegającego na realizacji umowy z tym klientem (art. 6 ust. 1 lit. f RODO).
- Logowanie, w tym przez Google lub Microsoft, oraz wysyłanie wiadomości niezbędnych do korzystania z konta (potwierdzenie adresu e-mail, link logowania, zmiana hasła, zaproszenia do organizacji) na tej samej podstawie.
- Zapewnienie bezpieczeństwa usługi, zapobieganie nadużyciom i diagnozowanie błędów na podstawie prawnie uzasadnionego interesu (art. 6 ust. 1 lit. f RODO).
- Rozliczenia i obowiązki księgowe oraz podatkowe na podstawie obowiązku prawnego (art. 6 ust. 1 lit. c RODO).
- Ustalenie, dochodzenie lub obrona roszczeń na podstawie prawnie uzasadnionego interesu (art. 6 ust. 1 lit. f RODO).
- Kontakt w odpowiedzi na Twoje wiadomości na podstawie prawnie uzasadnionego interesu (art. 6 ust. 1 lit. f RODO).

## 4. Odbiorcy danych

Korzystamy z dostawców, którzy przetwarzają dane w naszym imieniu na podstawie umów powierzenia, wyłącznie w zakresie potrzebnym do świadczenia usługi:

- Fly.io, Inc. (USA): hosting aplikacji; serwery działają we Frankfurcie (Niemcy).
- Neon (Databricks, Inc., USA): baza danych; dane przechowujemy w Londynie (Wielka Brytania).
- Resend (Plus Five Five, Inc., USA): wysyłka wiadomości e-mail związanych z kontem.
- Anthropic PBC, OpenAI (OpenAI Ireland Ltd) oraz Google (Google Ireland Limited): modele sztucznej inteligencji wykorzystywane do analizy dokumentów planistycznych i danych o działkach, w tym treści, które wprowadzasz do funkcji opartych na AI.

Jeśli logujesz się przez Google (Google Ireland Limited) lub Microsoft (Microsoft Ireland Operations Limited), ten dostawca uwierzytelnia Cię jako niezależny administrator i przekazuje nam Twoje imię i nazwisko, adres e-mail oraz identyfikator konta. Przetwarzanie danych przez tych dostawców opisują ich własne polityki prywatności.

Mapa w Plotea pobiera podkłady bezpośrednio z serwisów OpenFreeMap, OpenStreetMap i Geoportal (Główny Urząd Geodezji i Kartografii), które w związku z tym otrzymują adres IP Twojej przeglądarki.

Dane mogą być też udostępnione organom publicznym, gdy wymaga tego prawo.

## 5. Przekazywanie danych poza EOG

Część dostawców ma siedzibę w USA, a baza danych znajduje się w Wielkiej Brytanii, wobec której Komisja Europejska stwierdziła odpowiedni stopień ochrony danych. Do USA dane przekazujemy na podstawie decyzji Komisji Europejskiej stwierdzającej odpowiedni stopień ochrony (EU–US Data Privacy Framework) wobec certyfikowanych dostawców lub na podstawie standardowych klauzul umownych zatwierdzonych przez Komisję Europejską. Kopię zabezpieczeń możesz uzyskać, pisząc na ${CONTACT}.

## 6. Okres przechowywania

- Dane konta i dane korzystania z usługi: przez czas posiadania konta, a po jego usunięciu do 30 dni (kopie zapasowe do 90 dni).
- Dzienniki techniczne i bezpieczeństwa: do 90 dni.
- Dokumenty rozliczeniowe: przez okres wymagany przepisami podatkowymi (co do zasady 5 lat od końca roku podatkowego).
- Dane potrzebne do obrony roszczeń: do upływu okresu przedawnienia.

## 7. Twoje prawa

Masz prawo do dostępu do danych, ich sprostowania, usunięcia, ograniczenia przetwarzania, przenoszenia danych oraz prawo sprzeciwu wobec przetwarzania opartego na prawnie uzasadnionym interesie. Aby skorzystać z tych praw, napisz na ${CONTACT}. Masz też prawo wnieść skargę do Prezesa Urzędu Ochrony Danych Osobowych (ul. Stawki 2, 00-193 Warszawa).

Podanie danych jest dobrowolne, ale bez adresu e-mail nie można założyć konta ani korzystać z usługi. Nie podejmujemy wobec Ciebie decyzji opartych wyłącznie na zautomatyzowanym przetwarzaniu, które wywołują skutki prawne.

## 8. Pliki cookie i pamięć przeglądarki

Plotea używa wyłącznie niezbędnych plików cookie sesji logowania oraz pamięci przeglądarki do zapamiętania ustawień interfejsu (np. motywu). Nie używamy plików cookie reklamowych ani analitycznych podmiotów trzecich.

## 9. Zmiany polityki

O istotnych zmianach tej polityki poinformujemy w aplikacji lub e-mailem. Aktualna wersja jest zawsze dostępna pod adresem plotea.com/privacy.
`

export const ploteaTerms = `
# Regulamin usługi Plotea

## 1. Postanowienia ogólne

Regulamin określa zasady korzystania z usługi Plotea, dostępnej pod adresem plotea.com („Usługa”), świadczonej przez ${COMPANY} („Usługodawca”). Kontakt: ${CONTACT}.

Usługa jest przeznaczona wyłącznie dla przedsiębiorców i innych profesjonalnych użytkowników, którzy korzystają z niej w związku z działalnością gospodarczą lub zawodową. Usługa nie jest kierowana do konsumentów.

## 2. Definicje

- Klient: przedsiębiorca, który zawarł z Usługodawcą umowę o korzystanie z Usługi.
- Użytkownik: osoba fizyczna korzystająca z Usługi w imieniu Klienta na podstawie założonego konta.
- Organizacja: przestrzeń w Usłudze przypisana Klientowi, do której należą Użytkownicy.
- Konto: zbiór danych i uprawnień Użytkownika w Usłudze.

## 3. Zamknięta przedsprzedaż

Usługa jest obecnie udostępniana w ramach zamkniętej przedsprzedaży. Dostęp do pełnej funkcjonalności otrzymują Organizacje, którym Usługodawca przyznał plan na podstawie odrębnego zamówienia lub umowy. Zakres funkcji, limity i wynagrodzenie określa to zamówienie lub umowa.

W okresie przedsprzedaży Usługa jest rozwijana: funkcje mogą się zmieniać, a niektóre mogą być oznaczone jako wersje testowe. Usługodawca może wstrzymać rejestrację nowych kont.

## 4. Konto i logowanie

- Konto można założyć, podając adres e-mail i hasło lub logując się przez konto Google albo Microsoft.
- Użytkownik odpowiada za poufność swoich danych logowania i niezwłocznie informuje Usługodawcę o nieuprawnionym dostępie do Konta.
- Klient odpowiada za działania Użytkowników swojej Organizacji oraz za nadawanie i odbieranie im dostępu.

## 5. Zakres Usługi

Usługa pozwala analizować działki na podstawie danych publicznych, w tym miejscowych planów zagospodarowania przestrzennego, ewidencji gruntów i budynków, rejestru cen nieruchomości oraz innych rejestrów i serwisów mapowych, a także przygotowywać koncepcje zabudowy i zestawienia.

Wyniki Usługi mają charakter informacyjny i pomocniczy. Nie stanowią porady prawnej, projektu budowlanego, wyceny nieruchomości ani opinii uprawnionego architekta, rzeczoznawcy lub prawnika. Część wyników powstaje z użyciem modeli sztucznej inteligencji i może zawierać błędy. Przed podjęciem decyzji Klient powinien zweryfikować wyniki w źródłowych dokumentach i u właściwych organów.

## 6. Zasady korzystania

Zabronione jest w szczególności: korzystanie z Usługi niezgodnie z prawem, udostępnianie kont osobom spoza Organizacji, masowe pobieranie danych z Usługi w sposób automatyczny bez zgody Usługodawcy, próby obejścia zabezpieczeń oraz wprowadzanie treści naruszających prawa osób trzecich.

## 7. Wynagrodzenie

Wysokość i terminy płatności określa zamówienie lub umowa zawarta z Klientem. Usługodawca wystawia faktury elektroniczne, na co Klient wyraża zgodę.

## 8. Dostępność i odpowiedzialność

- Usługodawca dokłada starań, aby Usługa działała nieprzerwanie, ale może ją czasowo wyłączyć w celu konserwacji lub z powodu awarii. Usługodawca nie odpowiada za niedostępność danych dostarczanych przez podmioty zewnętrzne, w tym serwisy publiczne.
- Odpowiedzialność Usługodawcy wobec Klienta jest ograniczona do szkody rzeczywistej i do wysokości wynagrodzenia zapłaconego przez Klienta za ostatnie 12 miesięcy, a w okresie bezpłatnego dostępu wyłączona w granicach dopuszczalnych przez prawo. Ograniczenie nie dotyczy szkód wyrządzonych umyślnie.
- Usługodawca nie odpowiada za decyzje podjęte przez Klienta na podstawie wyników Usługi.

## 9. Własność intelektualna i dane Klienta

Usługa, jej oprogramowanie i wygląd należą do Usługodawcy. Klient otrzymuje niewyłączne, nieprzenoszalne prawo korzystania z Usługi na czas trwania umowy. Treści i dane wprowadzone przez Klienta pozostają jego własnością; Usługodawca przetwarza je wyłącznie w celu świadczenia Usługi. Wyniki analiz Klient może wykorzystywać we własnej działalności.

## 10. Dane osobowe

Zasady przetwarzania danych osobowych opisuje Polityka prywatności dostępna pod adresem plotea.com/privacy. Jeżeli Klient powierza Usługodawcy przetwarzanie danych osobowych, strony zawierają umowę powierzenia.

## 11. Rozwiązanie umowy i usunięcie Konta

Użytkownik może w każdej chwili poprosić o usunięcie Konta. Właściciel Organizacji może usunąć Organizację. Usługodawca może zablokować Konto lub rozwiązać umowę ze skutkiem natychmiastowym w razie istotnego naruszenia Regulaminu. Okres wypowiedzenia umów płatnych określa zamówienie lub umowa.

## 12. Reklamacje

Reklamacje dotyczące Usługi można zgłaszać na ${CONTACT}. Usługodawca odpowiada w ciągu 14 dni.

## 13. Zmiany Regulaminu i postanowienia końcowe

Usługodawca może zmienić Regulamin z ważnych przyczyn, informując Klientów z co najmniej 14-dniowym wyprzedzeniem. W sprawach nieuregulowanych stosuje się prawo polskie. Spory rozstrzyga sąd właściwy dla siedziby Usługodawcy.
`
